import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dropdown, Modal, Spin, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { MenuProps } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import IssueFilters, { IssueListFilters } from "../components/IssueFilters";
import IssueFormModal from "../components/IssueFormModal";
import IssueKeyBadge from "../components/IssueKeyBadge";
import UserAvatar from "../../components/UserAvatar";
import { createIssue, deleteIssue, fetchIssues, fetchLabels } from "../../../../core/data/redux/projects/thunks";
import { selectCurrentProject, selectIssuesByProject, selectIssuesLoading, selectLabels, selectStatuses } from "../../../../core/data/redux/projects/selectors";
import { ISSUE_TYPES, PRIORITY_OPTIONS, STATUS_CATEGORY_COLORS } from "../../constants/projects.constants";
import { useProjectCapability } from "../../hooks/useProjectRole";
import { all_routes } from "../../../router/all_routes";
import { errMsg } from "../../helpers/errMsg.helper";
import { CONFIRM_MODAL_PROPS } from "../../helpers/modal.style";
import { notify } from "../../../../services/toast.config";
import type { Issue } from "../../../../core/data/redux/projects/types";
import type { AppDispatch } from "../../../../core/data/redux/store";
import { humanReadableDate } from "../../../../helper/dateTimeHelper";

const INITIAL_FILTERS: IssueListFilters = { q: "", type: null, status_id: null, assignee_id: null, epic_id: null, label_ids: [] };

const IssuesList: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const project = useSelector(selectCurrentProject);
  const statuses = useSelector(selectStatuses);
  const labels = useSelector(selectLabels);
  const issuesLoading = useSelector(selectIssuesLoading);
  const projectIssues = useSelector(selectIssuesByProject(project?.id ?? null));
  const canCreateIssue = useProjectCapability(project?.id, "CREATE_ISSUE");
  const canDeleteIssue = useProjectCapability(project?.id, "DELETE_ISSUE");
  const [filters, setFilters] = useState<IssueListFilters>(INITIAL_FILTERS);
  const [createOpen, setCreateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reload = useCallback(() => {
    if (!project?.id) return;
    const p: Record<string, any> = { project_id: project.id };
    if (filters.q) p.q = filters.q;
    if (filters.type) p.type = filters.type;
    if (filters.status_id) p.status_id = filters.status_id;
    if (filters.assignee_id) p.assignee_id = filters.assignee_id;
    if (filters.epic_id) p.epic_id = filters.epic_id;
    if (filters.label_ids.length > 0) p.label_ids = filters.label_ids;
    dispatch(fetchIssues(p));
  }, [dispatch, project?.id, filters]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { if (project?.id) dispatch(fetchLabels(project.id)); }, [dispatch, project?.id]);

  const filtered = useMemo(() => {
    let list = (projectIssues || []).filter((i) => i.is_active);
    if (filters.q) { const q = filters.q.toLowerCase(); list = list.filter((i) => i.title.toLowerCase().includes(q) || i.issue_key.toLowerCase().includes(q)); }
    if (filters.type) list = list.filter((i) => i.type === filters.type);
    if (filters.status_id) list = list.filter((i) => i.status_id === filters.status_id);
    if (filters.assignee_id) list = list.filter((i) => Number(i.assignee_id) === filters.assignee_id);
    if (filters.epic_id) list = list.filter((i) => i.epic_id === filters.epic_id);
    if (filters.label_ids.length > 0) { const s = new Set(filters.label_ids); list = list.filter((i) => (i.labels || []).some((l) => s.has(l.id))); }
    return list;
  }, [projectIssues, filters]);

  const statusMap = useMemo(() => new Map(statuses.map((s) => [s.id, s])), [statuses]);

  const handleCreate = useCallback(async (payload: Record<string, any>) => {
    setSubmitting(true);
    try { await dispatch(createIssue(payload)).unwrap(); reload(); notify.success("Issue created"); setCreateOpen(false); }
    catch (e: any) { notify.error(errMsg(e, "Failed to create issue")); throw e; }
    finally { setSubmitting(false); }
  }, [dispatch, reload]);

  const handleDelete = useCallback((issue: Issue) => {
    Modal.confirm({
      title: `Delete ${issue.issue_key}?`, content: "This issue will be deactivated.", okText: "Delete", okButtonProps: { danger: true },
      ...CONFIRM_MODAL_PROPS,
      onOk: async () => { try { await dispatch(deleteIssue(issue.id)).unwrap(); notify.success("Issue deleted"); } catch (e: any) { notify.error(errMsg(e, "Failed to delete issue")); } },
    });
  }, [dispatch]);

  const goToDetail = (issue: Issue) => { if (!project) return; navigate(all_routes.projectIssueDetails.replace(":projectKey", project.key).replace(":issueKey", issue.issue_key)); };

  const columns: ColumnsType<Issue> = [
    {
      title: <Tooltip title="Issue Type">Type</Tooltip>, dataIndex: "type", key: "type", width: 90,
      render: (type) => { const m = ISSUE_TYPES.find((t) => t.value === type); return (<Tooltip title={m?.label || type}><span className="d-inline-flex align-items-center gap-1 small" style={{ color: m?.color }}><span className="d-inline-block rounded-circle" style={{ width: 8, height: 8, background: m?.color || "#6B7280" }} />{m?.label || type}</span></Tooltip>); },
    },
    { title: <Tooltip title="Issue Key">Key</Tooltip>, dataIndex: "issue_key", key: "issue_key", width: 110, render: (k, i) => project ? <IssueKeyBadge projectKey={project.key} issueKey={i.issue_key} /> : k },
    { title: <Tooltip title="Issue Title">Title</Tooltip>, dataIndex: "title", key: "title", render: (t, i) => (<Tooltip title={t}><span className="fw-medium" onClick={() => goToDetail(i)} style={{ cursor: "pointer", color: "#0f172a" }}>{t}</span></Tooltip>) },
    {
      title: <Tooltip title="Current Status">Status</Tooltip>, dataIndex: "status_id", key: "status_id", width: 160,
      render: (sid) => { const st = statusMap.get(sid); if (!st) return "—"; return (<Tooltip title={st.name}><Tag style={{ background: st.color || STATUS_CATEGORY_COLORS[st.category], color: "#fff", border: "none", margin: 0 }}>{st.name}</Tag></Tooltip>); },
    },
    {
      title: <Tooltip title="Priority">Priority</Tooltip>, dataIndex: "priority", key: "priority", width: 110,
      render: (p) => { const m = PRIORITY_OPTIONS.find((o) => o.value === p); return (<Tooltip title={m?.label || p}><span className="d-inline-flex align-items-center gap-1 small"><span className="d-inline-block rounded-circle" style={{ width: 8, height: 8, background: m?.color || "#6B7280" }} />{m?.label || p}</span></Tooltip>); },
    },
    {
      title: <Tooltip title="Assigned To">Assignee</Tooltip>, dataIndex: "assignee_snapshot", key: "assignee", width: 180,
      render: (snap, i) => { if (!i.assignee_id) return <span className="text-muted small">Unassigned</span>; const n = snap?.name || `User #${i.assignee_id}`; return (<span className="d-inline-flex align-items-center gap-2"><UserAvatar name={n} img={snap?.img} size={24} fontSize={11} /><span className="small">{n}</span></span>); },
    },
    { title: <Tooltip title="Due Date">Due</Tooltip>, dataIndex: "due_date", key: "due_date", width: 110, render: (d) => d ? <Tooltip title={humanReadableDate(d)}><span className="text-muted" style={{ fontSize: 12.5 }}><i className="ti ti-calendar me-1" />{humanReadableDate(d)}</span></Tooltip> : "—" },
    {
      title: "Actions", key: "actions", width: 80, align: "center",
      render: (_, issue) => {
        const items: MenuProps["items"] = [{ key: "open", icon: <i className="ti ti-external-link" style={{ color: "#3b82f6" }} />, label: "Open", onClick: () => goToDetail(issue) }];
        if (canDeleteIssue) {
          items.push({ type: "divider" });
          items.push({ key: "delete", icon: <i className="ti ti-trash" />, danger: true, label: "Delete", onClick: () => handleDelete(issue) });
        }
        return (<Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight"><button type="button" className="btn btn-icon btn-sm btn-light" style={{ borderRadius: 6 }}><i className="ti ti-dots-vertical" /></button></Dropdown>);
      },
    },
  ];

  if (!project) return (<div className="d-flex justify-content-center align-items-center bg-white" style={{ minHeight: 300, borderRadius: 12 }}><div className="text-center"><div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }} /><p className="mt-3 text-muted">Loading project...</p></div></div>);

  return (
    <div>
      <div className="card border-0 mb-3" style={{ borderRadius: 12, boxShadow: "0 1px 6px rgba(0,0,0,0.05)" }}>
        <div className="card-header border-0">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h4 className="mb-1 d-flex align-items-center gap-2" style={{ whiteSpace: "nowrap" }}>
                <i className="ti ti-bug" style={{ color: "#3b82f6", fontSize: 22, flexShrink: 0 }} />
                <span>Issues</span>
                <p className="text-muted mb-0" style={{ fontSize: 13 }}>({filtered.length} issue{filtered.length === 1 ? "" : "s"})</p>
              </h4>

            </div>
            {canCreateIssue && (
              <button type="button" className="btn btn-info btn-sm d-inline-flex align-items-center gap-1" style={{ borderRadius: 8, whiteSpace: "nowrap" }} onClick={() => setCreateOpen(true)}>
                <i className="ti ti-plus" style={{ fontSize: 16 }} />
                <span>New Issue</span>
              </button>
            )}
          </div>
        </div>
      </div>
      <IssueFilters filters={filters} statuses={statuses} labels={labels} projectId={project?.id} onFilterChange={(partial) => setFilters((p) => ({ ...p, ...partial }))} />
      <div className="card-body p-0 mb-3 border-0">
        {issuesLoading && filtered.length === 0 ? (
          <div className="d-flex justify-content-center align-items-center bg-white" style={{ minHeight: 400, borderRadius: 12 }}><div className="text-center"><div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }} /><p className="mt-3 text-muted">Loading issues...</p></div></div>
        ) : filtered.length > 0 ? (
          <div className="bg-white" style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #e2e8f0" }}>
            <Table
              className="custom-data-table"
              rowKey="id"
              columns={columns}
              dataSource={filtered}
              loading={issuesLoading}
              size="middle"
              scroll={{ x: 1100 }}
              rowClassName={(_, i) => i % 2 === 0 ? "data-table-row-light" : "data-table-row-dark"}
              pagination={{
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
                defaultPageSize: 25,
                showTotal: (total, range) => (
                  <span style={{ fontSize: 13, color: "#64748b" }}>
                    {range[0]}–{range[1]} of <strong style={{ color: "#1e293b" }}>{total}</strong> issues
                  </span>
                ),
                style: {
                  padding: "12px 20px",
                  margin: 0,
                  borderTop: "1px solid #e2e8f0",
                  display: "flex",
                  alignItems: "center",
                  gap: 10
                },
                itemRender: (page, type, originalElement) => {
                  const base: React.CSSProperties = {
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 32,
                    minWidth: 32,
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 500,
                    border: "1px solid #e2e8f0",
                    background: "#fff",
                    color: "#475569",
                    cursor: "pointer",
                    userSelect: "none" as const,
                    padding: "0 10px",
                    gap: 10,
                    transition: "background 0.15s, border-color 0.15s",
                  };

                  if (type === "prev") {
                    return (
                      <span style={base}>
                        <i className="ti ti-chevron-left" style={{ fontSize: 14 }} />
                        Prev
                      </span>
                    );
                  }
                  if (type === "next") {
                    return (
                      <span style={base}>
                        Next
                        <i className="ti ti-chevron-right" style={{ fontSize: 14 }} />
                      </span>
                    );
                  }
                  if (type === "page") {
                    return (
                      <span style={{ ...base, padding: 0 }}>
                        {page}
                      </span>
                    );
                  }
                  if (type === "jump-prev" || type === "jump-next") {
                    return <span style={{ ...base, border: "none", background: "transparent", color: "#94a3b8" }}>•••</span>;
                  }
                  return originalElement;
                },
              }}
            />
          </div>
        ) : (
          <div className="d-flex justify-content-center align-items-center bg-white" style={{ minHeight: 300, borderRadius: 12 }}><div className="text-center"><i className="ti ti-file-search" style={{ fontSize: 48, color: "#cbd5e1" }} /><h5 className="mt-3 mb-2 fw-semibold">No Issues Found</h5><p className="text-muted mb-0">Try adjusting your filters or create a new issue.</p></div></div>
        )}
      </div>
      <IssueFormModal open={createOpen} submitting={submitting} onSubmit={handleCreate} onCancel={() => setCreateOpen(false)} />
    </div>
  );
};

export default IssuesList;
